// useListCharacter with useQuery hook, to fetch data from the API api/characters
// and return the data and the status of the request
import _ from 'lodash';
import { useEffect, useMemo } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import {
  getAllBookings,
  getBookingList,
  getAllBookingLinesByBookingId,
  PAGE_SIZE_BOOKING_LIST,
  getCustomersBookings
} from 'services/api/module/booking.api';
import { BookingLine, PaginationType } from 'services/api/type/booking.type';
import QUERY_KEYS from 'services/api/queryKeys';
import { toLocalTime } from 'utils';
import useBookingStore from 'stores/useBooking';

interface GroupedBookingLine extends BookingLine {
  isMatching?: boolean;
}

export const useGetBooking = (
  filterOptions: any,
  page: number = 1,
  pageSize: number = PAGE_SIZE_BOOKING_LIST,
  isEnable: boolean = true
) => {
  const {
    data: dataBookingList,
    status,
    isLoading,
    isFetching
  } = useQuery({
    queryKey: [QUERY_KEYS.KEY_ROOM, QUERY_KEYS.GET_BOOKING_LIST, page, pageSize],
    queryFn: () => getBookingList(filterOptions, page, pageSize),
    enabled: !!page || isEnable, // TODO: temporary add this for enhance performance
    placeholderData: keepPreviousData
  });

  const { setPageBookingList } = useBookingStore();

  useEffect(() => {
    setPageBookingList(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const cookedData = useMemo(() => {
    const bookingList: BookingLine[] = _.get(dataBookingList, 'data.data', []);

    if (!bookingList.length) return [];

    // Pre-sort once and process time conversion
    const sortedBookings = bookingList
      .map((item: BookingLine) => ({
        ...item,
        check_in: toLocalTime(item.check_in),
        check_out: toLocalTime(item.check_out),
        isMatching: item.booking_id === item.moved_booking_id
      }))
      .sort((a, b) => a.booking_line_id - b.booking_line_id);

    // Group and build expandable rows in single pass
    const groups = new Map<number, BookingLine[]>();

    for (const item of sortedBookings) {
      const groupKey = item.moved_booking_id;
      if (!groups.has(groupKey)) {
        groups.set(groupKey, []);
      }
      const groupItems = groups.get(groupKey);
      if (groupItems) {
        groupItems.push(item);
      }
    }

    // Process groups and create final result
    const result: BookingLine[] = [];

    for (const groupItems of Array.from(groups.values())) {
      if (groupItems.length === 1) {
        // Single item - no expandable rows needed
        const item = groupItems[0];
        delete (item as GroupedBookingLine).isMatching;
        result.push(item);
      } else {
        // Multiple items - create expandable structure
        // Sort: matching bookings first, then mismatched ones
        groupItems.sort(
          (a: GroupedBookingLine, b: GroupedBookingLine) =>
            Number(b.isMatching) - Number(a.isMatching)
        );

        const mainItem = { ...groupItems[0] };
        delete (mainItem as GroupedBookingLine).isMatching;

        // Clean up isMatching property from all items
        const expandableRows = groupItems.map((item: GroupedBookingLine) => {
          const cleanItem = { ...item };
          delete cleanItem.isMatching;
          return cleanItem;
        });

        mainItem.expandableRows = expandableRows;
        result.push(mainItem);
      }
    }

    // Final sort by booking_id descending
    return result.sort((a, b) => b.booking_id - a.booking_id);
  }, [dataBookingList]);

  return {
    data: cookedData,
    pagination: _.get(dataBookingList, 'data.pagination', {}) as PaginationType,
    status,
    isLoading,
    isFetching
  };
};

export const useGetAllBooking = (filterOptions: any, isEnable: boolean = true) => {
  const {
    data: dataBookingList,
    status,
    isLoading,
    isFetching
  } = useQuery({
    queryKey: [QUERY_KEYS.GET_BOOKING_LIST_ALL, filterOptions],
    queryFn: () => getAllBookings(filterOptions),
    enabled: isEnable && !!filterOptions
  });

  const sortedData = useMemo(() => {
    const bookingList: BookingLine[] = _.get(dataBookingList, 'data.data', []);
    return bookingList.sort((a: any, b: any) => b.booking_id - a.booking_id);
  }, [dataBookingList]);

  return {
    data: sortedData,
    pagination: _.get(dataBookingList, 'data.pagination', {}) as PaginationType,
    status,
    isLoading,
    isFetching
  };
};

export const useGetAllBookingLineByBookingId = (bookingId?: number, isEnable?: boolean) => {
  const {
    data: dataBookingList,
    status,
    isLoading,
    isFetching
  } = useQuery({
    queryKey: [QUERY_KEYS.KEY_ROOM, QUERY_KEYS.GET_ALL_BOOKING_LINES_BY_BOOKING_ID, bookingId],
    queryFn: () => getAllBookingLinesByBookingId(bookingId),
    enabled: !!bookingId && isEnable
  });

  const cookedData = useMemo(() => {
    const bookingList: BookingLine[] = _.clone(dataBookingList) || [];

    bookingList.forEach((item: BookingLine) => {
      item.check_in = toLocalTime(item.check_in);
      item.check_out = toLocalTime(item.check_out);
    });
    return bookingList.sort((a: BookingLine, b: BookingLine) => b.booking_id - a.booking_id);
  }, [dataBookingList]);

  return {
    data: cookedData,
    status,
    isLoading,
    isFetching
  };
};

export const useGetCustomersBookings = (customerId?: number, isEnable: boolean = true) => {
  const { data, status, isLoading, isFetching } = useQuery({
    queryKey: [QUERY_KEYS.GET_CUSTOMERS_BOOKINGS, customerId],
    queryFn: () => getCustomersBookings(customerId),
    enabled: !!customerId && isEnable
  });

  return {
    data,
    status,
    isLoading,
    isFetching
  };
};
